import { ClipboardEvent, KeyboardEvent, useEffect, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { useNumericLimit } from '../useNumericLimit';
import styles from './PunchupStepper.module.css';

export interface PunchupStepperProps {
	/** Current value. `null` shows an empty field — only in `editable` + `clearable` mode ("no limit"). */
	value: number | null;
	/** Called with the new (number) value on a button press or a committed edit. */
	onChange: (value: number) => void;
	/** `editable` + `clearable` only: called when the field is emptied ("no limit"). */
	onClear?: () => void;
	min?: number;
	/** When undefined, incrementing is unbounded. */
	max?: number;
	/** Increment / decrement / clamp granularity. Default 1. */
	step?: number;
	disabled?: boolean;
	/** Highlights the decrement button and value in danger red — use when the aggregate
	 *  quantity for this type exceeds available inventory, or to flag a validation error. */
	error?: boolean;
	/**
	 * Renders the centre as a typable integer input instead of a read-only display. Typing is committed
	 * (parsed + clamped to [min, max]) on blur; the −/+ buttons still adjust by `step`. Off by default,
	 * so the Send Tickets quantity steppers stay +/−-only and unchanged.
	 */
	editable?: boolean;
	/** `editable` only: allow clearing the field to empty → `onChange(null)` (e.g. "no limit"). */
	clearable?: boolean;
	/** `editable` only: placeholder shown when the field is empty (e.g. "No limit"). */
	placeholder?: string;
	/** `editable` only: id for the inner input so a `PunchupFieldLabel htmlFor` can target it. */
	id?: string;
}

/** Strip a raw string to an integer grammar: digits, plus a single leading `-` when negatives are allowed. */
const sanitizeInteger = (raw: string, allowNegative: boolean): string => {
	let cleaned = raw.replace(/[^0-9-]/g, '');
	const negative = allowNegative && cleaned.startsWith('-');
	cleaned = cleaned.replace(/-/g, '');
	return negative ? `-${cleaned}` : cleaned;
};

/**
 * PunchupStepper
 *
 * Inline −/+ quantity control styled with punchup tokens. Two modes from one component:
 *  - default (read-only centre): the +/−-only quantity control used on the Send Tickets form.
 *  - `editable`: the centre becomes a typable integer field (type a big number directly), with the
 *    same −/+ buttons. Used as the count widget inside `PunchupNumericInput` so every Punchup count
 *    field shares one stepper. Both modes clamp to [min, max] and pulse the bound on overshoot.
 *
 * Fully controlled (`value` / `onChange`) — no form context. Decrement is disabled at `min` (default 0);
 * increment is disabled at `max`. Both buttons show orange on hover/active per the punchup aesthetic.
 */
export const PunchupStepper = ({
	value,
	onChange,
	onClear,
	min = 0,
	max,
	step = 1,
	disabled = false,
	error = false,
	editable = false,
	clearable = false,
	placeholder,
	id,
}: PunchupStepperProps) => {
	const { clamp, pulse } = useNumericLimit({ min, max });

	// Local draft string for the editable centre — mirrors PunchupColorPicker / PunchupNumericInput:
	// while focused we never let an external value overwrite what's being typed; on blur we commit.
	const [draft, setDraft] = useState<string>(() => (value == null ? '' : String(value)));
	const [focused, setFocused] = useState(false);
	const [pulseKey, setPulseKey] = useState(0);
	const [announcement, setAnnouncement] = useState('');

	useEffect(() => {
		if (!focused) setDraft(value == null ? '' : String(value));
	}, [value, focused]);

	useEffect(() => {
		if (pulse === null) setAnnouncement('');
	}, [pulse]);

	// Commit a number to the form: clamp, announce + pulse when capped, emit. `setDraft` keeps the
	// visible text in sync after a clamp.
	const commit = (next: number) => {
		const clamped = clamp(next);
		onChange(clamped);
		setDraft(String(clamped));
		if (clamped !== next) {
			setPulseKey((k) => k + 1);
			setAnnouncement(
				clamped > next ? `Adjusted to the minimum of ${clamped}` : `Adjusted to the maximum of ${clamped}`
			);
		}
	};

	const allowNegative = min < 0;

	// Stepping baseline: from empty (clearable null), the first + lands on the floor (min) rather than min+step.
	// A clearable field can also decrement AT the floor — that clears it to empty ("no limit") so the −/+
	// buttons alone reach the empty state (no select-all-delete needed). A non-clearable field dead-ends at min.
	const canDecrement = !disabled && value != null && (value > min || (clearable && value <= min));
	const baseForIncrement = value ?? min;
	const canIncrement = !disabled && (max === undefined || baseForIncrement < max);

	const handleDecrement = () => {
		if (value == null) return;
		if (clearable && value <= min) {
			onClear?.();
			return;
		}
		commit(value - step);
	};
	const handleIncrement = () => {
		commit(value == null ? min : value + step);
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key.length > 1 || e.ctrlKey || e.metaKey || e.altKey) return; // control / navigation keys
		if (/[0-9]/.test(e.key)) return;
		if (e.key === '-' && allowNegative && !draft.includes('-')) return;
		e.preventDefault(); // block letters, dots, a second minus, symbols
	};

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		setDraft(sanitizeInteger(e.clipboardData.getData('text'), allowNegative));
	};

	const handleBlur = () => {
		setFocused(false);
		const cleaned = sanitizeInteger(draft, allowNegative);
		if (cleaned === '' || cleaned === '-') {
			// Empty: clear to "no limit" when allowed, otherwise revert to the last committed value.
			if (clearable) {
				onClear?.();
				setDraft('');
			} else {
				setDraft(value == null ? '' : String(value));
			}
			return;
		}
		const parsed = parseInt(cleaned, 10);
		if (Number.isNaN(parsed)) {
			setDraft(value == null ? '' : String(value));
			return;
		}
		commit(parsed);
	};

	return (
		<div className={styles.root}>
			<button
				type="button"
				className={[styles.button, error ? styles.buttonError : '', pulse === 'min' ? styles.pulse : '']
					.filter(Boolean)
					.join(' ')}
				key={`dec-${pulse === 'min' ? pulseKey : 'idle'}`}
				onClick={handleDecrement}
				disabled={!canDecrement}
				aria-label="Decrease"
			>
				<Minus size={14} />
			</button>

			{editable ? (
				<input
					id={id}
					type="text"
					inputMode="numeric"
					className={[styles.input, error ? styles.valueError : ''].filter(Boolean).join(' ')}
					value={draft}
					placeholder={placeholder}
					disabled={disabled}
					onFocus={() => setFocused(true)}
					onChange={(e) => setDraft(sanitizeInteger(e.target.value, allowNegative))}
					onKeyDown={handleKeyDown}
					onPaste={handlePaste}
					onBlur={handleBlur}
				/>
			) : (
				<span className={`${styles.value}${error ? ` ${styles.valueError}` : ''}`} aria-live="polite">
					{value}
				</span>
			)}

			<button
				type="button"
				className={[styles.button, pulse === 'max' ? styles.pulse : ''].filter(Boolean).join(' ')}
				key={`inc-${pulse === 'max' ? pulseKey : 'idle'}`}
				onClick={handleIncrement}
				disabled={!canIncrement}
				aria-label="Increase"
			>
				<Plus size={14} />
			</button>

			{editable && (
				<span className={styles.srOnly} aria-live="polite" role="status">
					{announcement}
				</span>
			)}
		</div>
	);
};
